import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import ChevronRight from '@jwp/ott-theme/assets/icons/chevron_right.svg?react';
import FilterMob from '@jwp/ott-theme/assets/icons/FilterMob.svg?react';
import { DateRangeType } from '@jwp/ott-common/src/stores/ReportsStore';
import useBreakpoint, { Breakpoint } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import { ReportableType } from '@jwp/ott-common/src/graphql/query/reportModeration.queries';

import Icon from '../Icon/Icon';
import Button from '../Button/Button';

import styles from './ReportsQueueListFilter.module.scss';

export type { DateRangeType } from '@jwp/ott-common/src/stores/ReportsStore';

interface Props {
  appliedDateRange: DateRangeType;
  appliedContentTypes: ReportableType[];
  onApply: (dateRange: DateRangeType, contentTypes: ReportableType[]) => void;
  className?: string;
}

const ReportsQueueListFilter: React.FC<Props> = ({
  appliedDateRange,
  appliedContentTypes,
  onApply,
  className,
}) => {
  const { t } = useTranslation(['user']);
  const breakpoint = useBreakpoint();
  const isMobileView = breakpoint < Breakpoint.md;
  const [isFilterOpen, setIsFilterOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Filter States
  const [tempDateRange, setTempDateRange] = React.useState<DateRangeType>(appliedDateRange);
  const [tempContentTypes, setTempContentTypes] = React.useState<ReportableType[]>(appliedContentTypes);

  React.useEffect(() => {
    if (isFilterOpen) {
      setTempDateRange(appliedDateRange);
      setTempContentTypes(appliedContentTypes);
    }
  }, [isFilterOpen, appliedDateRange, appliedContentTypes]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsFilterOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const hasChanges = React.useMemo(() => {
    const sortedTempTypes = [...tempContentTypes].sort((a, b) => a.localeCompare(b)).join(',');
    const sortedAppliedTypes = [...appliedContentTypes].sort((a, b) => a.localeCompare(b)).join(',');

    return (
      tempDateRange !== appliedDateRange ||
      sortedTempTypes !== sortedAppliedTypes
    );
  }, [tempDateRange, appliedDateRange, tempContentTypes, appliedContentTypes]);

  const handleApplyFilters = () => {
    onApply(tempDateRange, tempContentTypes);
    setIsFilterOpen(false);
  };

  const handleClearAll = () => {
    setTempDateRange(null);
    setTempContentTypes([]);
  };

  const handleContentTypeToggle = (type: ReportableType) => {
    setTempContentTypes((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]));
  };

  const CONTENT_TYPE_MAP: Record<ReportableType, string> = {
    [ReportableType.REEL]: t('user:reports.report_filter.reel'),
    [ReportableType.VIDEO]: t('user:reports.report_filter.video'),
    [ReportableType.CHANNEL]: t('user:reports.report_filter.channel'),
  };

  return (
    <div className={classNames(styles.filterWrapper, className)} ref={dropdownRef}>
      {isMobileView ? (
        <button
          type="button"
          className={classNames(styles.filterIconBtn, { [styles.open]: isFilterOpen })}
          onClick={() => setIsFilterOpen(!isFilterOpen)}
          aria-label={t('user:reports.report_filter.filter')}
        >
          <FilterMob className={styles.filterIcon} aria-hidden="true" />
        </button>
      ) : (
        <button
          type="button"
          className={classNames(styles.filterBtn, { [styles.open]: isFilterOpen })}
          onClick={() => setIsFilterOpen(!isFilterOpen)}
        >
          <span>{t('user:reports.report_filter.filter')}</span>
          <Icon icon={ChevronRight} className={styles.icon} />
        </button>
      )}
      {isFilterOpen && isMobileView && (
        <button
          type="button"
          className={styles.filterBackdrop}
          aria-label={t('user:reports.report_filter.filter')}
          onClick={() => setIsFilterOpen(false)}
        />
      )}
      {isFilterOpen && (
        <div className={classNames(styles.filterPopup, { [styles.filterPopupCentered]: isMobileView })}>
          <div className={styles.popupHeader}>
            <h3>{t('user:reports.report_filter.add_filters')}</h3>
            <button type="button" className={styles.clearBtn} onClick={handleClearAll}>
              {t('user:reports.report_filter.clear_all')}
            </button>
          </div>

          <div className={styles.filterPopupBody}>
            <div className={styles.filterSection}>
              <span className={styles.sectionLabel}>{t('user:reports.report_filter.content_type')}</span>
              <div className={styles.chipGroup}>
                {Object.values(ReportableType).map((type) => (
                  <button
                    key={type}
                    type="button"
                    className={classNames(styles.chip, { [styles.active]: tempContentTypes.includes(type) })}
                    onClick={() => handleContentTypeToggle(type)}
                  >
                    {CONTENT_TYPE_MAP[type]}
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.filterSection}>
              <span className={styles.sectionLabel}>{t('user:reports.report_filter.date_range')}</span>
              <div className={styles.chipGroup}>
                {(['today', 'lastWeek', 'lastMonth', 'oldest'] as DateRangeType[]).map((range) => {
                  const rangeLabels: Record<string, string> = {
                    today: t('user:reports.report_filter.today'),
                    lastWeek: t('user:reports.report_filter.last_week'),
                    lastMonth: t('user:reports.report_filter.last_month'),
                    oldest: t('user:reports.report_filter.oldest'),
                  };
                  return (
                    <button
                      key={range}
                      type="button"
                      className={classNames(styles.chip, { [styles.active]: tempDateRange === range })}
                      onClick={() => setTempDateRange((prev) => (prev === range ? null : range))}
                    >
                      {range ? rangeLabels[range] : ''}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className={styles.popupFooter}>
            <Button
              label={t('user:reports.report_filter.apply_filter')}
              color="primary"
              variant="contained"
              fullWidth
              className={styles.applyBtn}
              onClick={handleApplyFilters}
              disabled={!hasChanges}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsQueueListFilter;